import type { FC, ReactNode } from 'react'

import Image from 'next/image'

interface PageHeroProps {
    eyebrow: string
    title: ReactNode
    description: ReactNode
    image?: string
    imageAlt?: string
    children?: ReactNode
}

export const PageHero: FC<PageHeroProps> = ({
    eyebrow,
    title,
    description,
    image,
    imageAlt = '',
    children
}): ReactNode => {
    return (
        <section id='page-hero' className='surface-night relative isolate overflow-hidden pt-36 pb-20 text-white sm:pb-24 lg:pt-48 lg:pb-28'>
            {image && (
                <div aria-hidden={imageAlt ? undefined : 'true'} className='absolute inset-0 -z-10'>
                    <Image
                        src={image}
                        alt={imageAlt}
                        fill
                        preload
                        sizes='100vw'
                        className='object-cover opacity-35'
                    />
                    <div className='absolute inset-0 bg-linear-to-b from-night/70 via-night/60 to-night' />
                    <div className='absolute inset-0 bg-linear-to-r from-night via-night/60 to-transparent' />
                </div>
            )}

            <div aria-hidden='true' className='pointer-events-none absolute -top-40 right-[-10%] -z-10 size-[36rem] rounded-full bg-crimson/25 blur-[120px] motion-safe:animate-glow' />

            <div className='mx-auto max-w-7xl px-6'>
                <p className='inline-flex items-center gap-3 text-xs font-bold tracking-[0.24em] text-gold uppercase animate-in fade-in slide-in-from-bottom-2 duration-700'>
                    <span aria-hidden='true' className='h-px w-8 bg-gold/60' />
                    {eyebrow}
                </p>

                <h1 className='mt-5 max-w-4xl font-heading text-5xl leading-[1.02] tracking-tight text-balance animate-in fade-in slide-in-from-bottom-4 duration-700 sm:text-6xl lg:text-7xl'>
                    {title}
                </h1>

                <p className='mt-6 max-w-2xl text-lg leading-relaxed text-white/75 animate-in fade-in slide-in-from-bottom-4 delay-150 duration-700 fill-mode-both'>
                    {description}
                </p>

                {children && (
                    <div className='mt-9 animate-in fade-in slide-in-from-bottom-4 delay-300 duration-700 fill-mode-both'>
                        {children}
                    </div>
                )}
            </div>
        </section>
    )
}
